import React, { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Header } from '../Layout/Header'
import { Sidebar } from '../Layout/Sidebar'
import { MinimapModule } from './Modules/MinimapModule'
import { AnalyticsModule } from './Modules/AnalyticsModule'
import { ReportsModule } from './Modules/ReportsModule'
import { MachineManagerModule } from './Modules/MachineManagerModule'
import { TaskManagerModule } from './Modules/TaskManagerModule'
import { KpiBar } from './KpiBar'
import { MobileMenuProvider, useMobileMenu } from '../../context/MobileMenuContext'
import { Activity, CalendarDays } from 'lucide-react'

const AdminDashboardContent: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { isOpen, toggleMenu, closeMenu } = useMobileMenu()
  const [activeModule, setActiveModule] = useState('minimap')
  const [availableHalls, setAvailableHalls] = useState<string[]>([])
  const [filters, setFilters] = useState({
    search: '',
    showProblems: false,
    showTasks: false,
    selectedHal: undefined,
    selectedStatus: undefined
  })

  const moduleCopy: Record<string, { title: string; description: string }> = {
    minimap: { title: 'Machinepark', description: 'Live inzicht in productie, inspecties en operationele aandachtspunten.' },
    tasks: { title: 'Takenbeheer', description: 'Plan, verdeel en volg werkzaamheden binnen het team.' },
    analytics: { title: 'Kwaliteitsanalyse', description: 'Ontdek trends en bewaak de kwaliteit van de productie.' },
    reports: { title: 'Rapporten & export', description: 'Maak heldere overzichten voor overleg en opvolging.' },
    machines: { title: 'Machinebeheer', description: 'Beheer machines, locaties en actuele statussen.' },
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const renderActiveModule = () => {
    switch (activeModule) {
      case 'tasks':
        return <TaskManagerModule />
      case 'analytics':
        return <AnalyticsModule />
      case 'reports':
        return <ReportsModule />
      case 'machines':
        return <MachineManagerModule />
      case 'minimap':
      default:
        return <MinimapModule filters={filters} onHallsLoaded={setAvailableHalls} />
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Laden...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col font-sans transition-colors overflow-x-hidden" style={{ background: 'var(--bg)' }}>
      <Header
        title="Operations dashboard"
        onLogout={handleLogout}
        showMenuButton={true}
        onMenuToggle={toggleMenu}
      />

      <main className="w-full max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-7 lg:py-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3 mb-5 sm:mb-6">
          <div>
            <div className="eyebrow flex items-center gap-2"><Activity size={13} /> Operationeel overzicht</div>
            <h2 className="page-title mt-2">Goedendag, {user.name?.split(' ')[0]}</h2>
            <p className="page-subtitle mt-1">Direct zicht op wat er vandaag speelt binnen KVH.</p>
          </div>
          <div className="date-chip"><CalendarDays size={15} /> {new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())}</div>
        </div>

        <KpiBar />

        <div className="flex flex-col md:flex-row mt-5 sm:mt-6 gap-4 sm:gap-5 lg:gap-6 items-start">
          <Sidebar
            activeModule={activeModule}
            onModuleChange={setActiveModule}
            showFilters={activeModule === 'minimap'}
            onFilterChange={(f: any) => setFilters(prev => ({ ...prev, ...f }))}
            availableHalls={availableHalls}
            isOpen={isOpen}
            onClose={closeMenu}
          />

          <section className="workspace-surface w-full min-w-0 relative overflow-x-hidden">
            <div className="workspace-heading">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold tracking-[-0.02em]" style={{ color: 'var(--text-primary)' }}>{moduleCopy[activeModule].title}</h2>
                <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{moduleCopy[activeModule].description}</p>
              </div>
            </div>
            <div className="p-3 sm:p-5 lg:p-6">{renderActiveModule()}</div>
          </section>
        </div>
      </main>
    </div>
  )
}

export const AdminDashboard: React.FC = () => (
  <MobileMenuProvider>
    <AdminDashboardContent />
  </MobileMenuProvider>
)
